import React, { useEffect, useState } from 'react';
import { OAuthLoginButton } from './OAuthLoginButton';
import styles from './OAuthButtonGroup.module.css';

interface OAuthButtonGroupProps {
  className?: string;
  disabled?: boolean;
}

interface PartnerSso {
  id: number;
  provider: string;
  displayName: string;
}

export const OAuthButtonGroup: React.FC<OAuthButtonGroupProps> = ({
  className = '',
  disabled = false,
}) => {
  const [available, setAvailable] = useState<Set<string> | null>(null);
  const [partnerSso, setPartnerSso] = useState<PartnerSso[]>([]);

  useEffect(() => {
    fetch('/_api/auth/oauth_providers')
      .then((r) => r.json())
      .then((d: { providers: string[]; partnerSso?: PartnerSso[] }) => {
        setAvailable(new Set(d.providers));
        if (d.partnerSso?.length) setPartnerSso(d.partnerSso);
      })
      .catch(() => setAvailable(new Set(['google', 'microsoft', 'github'])));
  }, []);

  // Always show core providers
  const show = (p: string) => {
    if (['google', 'microsoft', 'github'].includes(p)) return true;
    return available === null || available.has(p);
  };

  return (
    <div className={`${styles.container} ${className}`}>
      {/* Partner-specific SSO connectors — shown first and prominently */}
      {partnerSso.length > 0 && (
        <>
          <div className={styles.divider}>
            <span className={styles.dividerText}>sign in with your organization</span>
          </div>
          <div className={styles.buttons}>
            {partnerSso.map((sso) => (
              <OAuthLoginButton
                key={sso.id}
                provider={sso.provider}
                ssoConfigId={sso.id}
                disabled={disabled}
                className={styles.oktaButton}
              >
                {sso.provider === 'okta' && (
                  <svg className={styles.icon} viewBox="0 0 36 36" width="20" height="20">
                    <circle cx="18" cy="18" r="15" fill="#007dc1" />
                    <circle cx="18" cy="18" r="7" fill="#fff" />
                  </svg>
                )}
                {sso.provider === 'auth0' && (
                  <img src="/auth0-icon-onlight.svg" alt="" className={styles.icon} />
                )}
                {sso.displayName}
              </OAuthLoginButton>
            ))}
          </div>
        </>
      )}
      <div className={styles.divider}>
        <span className={styles.dividerText}>or continue with</span>
      </div>
      <div className={styles.buttons}>
        {show('google') && (
          <OAuthLoginButton provider="google" disabled={disabled} className={styles.googleButton}>
            <svg className={styles.icon} viewBox="0 0 24 24" width="20" height="20">
              <path
                fill="#4285F4"
                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
              />
              <path
                fill="#34A853"
                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
              />
              <path
                fill="#FBBC05"
                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
              />
              <path
                fill="#EA4335"
                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
              />
            </svg>
            Google
          </OAuthLoginButton>
        )}
        {show('microsoft') && (
          <OAuthLoginButton
            provider="microsoft"
            disabled={disabled}
            className={styles.microsoftButton}
          >
            <svg className={styles.icon} viewBox="0 0 24 24" width="20" height="20">
              <path fill="#F25022" d="M1 1h10v10H1z" />
              <path fill="#00A4EF" d="M1 13h10v10H1z" />
              <path fill="#7FBA00" d="M13 1h10v10H13z" />
              <path fill="#FFB900" d="M13 13h10v10H13z" />
            </svg>
            Microsoft
          </OAuthLoginButton>
        )}
        {show('github') && (
          <OAuthLoginButton provider="github" disabled={disabled} className={styles.githubButton}>
            <img
              src="/icons/GitHub_Invertocat_Black.png"
              alt="GitHub"
              width="20"
              height="20"
              className={styles.icon}
            />
            GitHub
          </OAuthLoginButton>
        )}

        {show('auth0') && (
          <OAuthLoginButton provider="auth0" disabled={disabled} className={styles.auth0Button}>
            <img src="/auth0-icon-onlight.svg" alt="Auth0" className={styles.icon} />
            Auth0
          </OAuthLoginButton>
        )}
        {show('okta') && (
          <OAuthLoginButton provider="okta" disabled={disabled} className={styles.oktaButton}>
            <svg className={styles.icon} viewBox="0 0 36 36" width="20" height="20">
              <path d="M19.8,.26l-.74,9.12c-.35-.04-.7-.06-1.06-.06-.45,0-.89,.03-1.32,.1l-.42-4.42c-.01-.14,.1-.26,.24-.26h.75l-.36-4.47c-.01-.14,.1-.26,.23-.26h2.45c.14,0,.25,.12,.23,.26h0Zm-6.18,.45c-.04-.13-.18-.21-.31-.16l-2.3,.84c-.13,.05-.19,.2-.13,.32l1.87,4.08-.71,.26c-.13,.05-.19,.2-.13,.32l1.91,4.01c.69-.38,1.44-.67,2.23-.85L13.63,.71ZM7.98,3.25l5.29,7.46c-.67,.44-1.28,.96-1.8,1.56l-3.17-3.12c-.1-.1-.09-.26,.01-.35l.58-.48-3.15-3.19c-.1-.1-.09-.26,.02-.35l1.87-1.57c.11-.09,.26-.07,.34,.04ZM3.54,7.57c-.11-.08-.27-.04-.34,.08l-1.22,2.12c-.07,.12-.02,.27,.1,.33l4.06,1.92-.38,.65c-.07,.12-.02,.28,.11,.33l4.04,1.85c.29-.75,.68-1.45,1.16-2.08L3.54,7.57ZM.55,13.33c.02-.14,.16-.22,.29-.19l8.85,2.31c-.23,.75-.36,1.54-.38,2.36l-4.43-.36c-.14-.01-.24-.14-.21-.28l.13-.74-4.47-.42c-.14-.01-.23-.14-.21-.28l.42-2.41h0Zm-.33,5.98c-.14,.01-.23,.14-.21,.28l.43,2.41c.02,.14,.16,.22,.29,.19l4.34-1.13,.13,.74c.02,.14,.16,.22,.29,.19l4.28-1.18c-.25-.74-.41-1.53-.45-2.34L.21,19.31Zm1.42,6.34c-.07-.12-.02-.27,.1-.33l8.26-3.92c.31,.74,.73,1.43,1.23,2.05l-3.62,2.58c-.11,.08-.27,.05-.34-.07l-.38-.66-3.69,2.55c-.11,.08-.27,.04-.34-.08l-1.23-2.12Zm10.01-1.72l-6.43,6.51c-.1,.1-.09,.26,.02,.35l1.88,1.57c.11,.09,.26,.07,.34-.04l2.6-3.66,.58,.49c.11,.09,.27,.07,.35-.05l2.52-3.66c-.68-.42-1.31-.93-1.85-1.51Zm-1.27,10.45c-.13-.05-.19-.2-.13-.32l3.81-8.32c.7,.36,1.46,.63,2.25,.78l-1.12,4.3c-.03,.13-.18,.21-.31,.16l-.71-.26-1.19,4.33c-.04,.13-.18,.21-.31,.16l-2.3-.84h0Zm6.56-7.75l-.74,9.12c-.01,.14,.1,.26,.23,.26h2.45c.14,0,.25-.12,.23-.26l-.36-4.47h.75c.14,0,.25-.12,.24-.26l-.42-4.42c-.43,.07-.87,.1-1.32,.1-.36,0-.71-.02-1.06-.07h0ZM25.76,1.94c.06-.13,0-.27-.13-.32l-2.3-.84c-.13-.05-.27,.03-.31,.16l-1.19,4.33-.71-.26c-.13-.05-.27,.03-.31,.16l-1.12,4.3c.8,.16,1.55,.43,2.25,.78L25.76,1.94h0Zm5.02,3.63l-6.43,6.51c-.54-.58-1.16-1.09-1.85-1.51l2.52-3.66c.08-.11,.24-.14,.35-.05l.58,.49,2.6-3.66c.08-.11,.24-.13,.34-.04l1.88,1.57c.11,.09,.11,.25,.02,.35Zm3.48,5.12c.13-.06,.17-.21,.1-.33l-1.23-2.12c-.07-.12-.23-.15-.34-.08l-3.69,2.55-.38-.65c-.07-.12-.23-.16-.34-.07l-3.62,2.58c.5,.62,.91,1.31,1.23,2.05l8.26-3.92Zm1.3,3.32l.42,2.41c.02,.14-.07,.26-.21,.28l-9.11,.85c-.04-.82-.2-1.6-.45-2.34l4.28-1.18c.13-.04,.27,.05,.29,.19l.13,.74,4.34-1.13c.13-.03,.27,.05,.29,.19h0Zm-.41,8.85c.13,.03,.27-.05,.29-.19l.42-2.41c.02-.14-.07-.26-.21-.28l-4.47-.42,.13-.74c.02-.14-.07-.26-.21-.28l-4.43-.36c-.02,.82-.15,1.61-.38,2.36l8.85,2.31h0Zm-2.36,5.5c-.07,.12-.23,.15-.34,.08l-7.53-5.2c.48-.63,.87-1.33,1.16-2.08l4.04,1.85c.13,.06,.18,.21,.11,.33l-.38,.65,4.06,1.92c.12,.06,.17,.21,.1,.33l-1.22,2.12h0Zm-10.07-3.07l5.29,7.46c.08,.11,.24,.13,.34,.04l1.87-1.57c.11-.09,.11-.25,.02-.35l-3.15-3.19,.58-.48c.11-.09,.11-.25,.01-.35l-3.17-3.12c-.53,.6-1.13,1.13-1.8,1.56h0Zm-.05,10.16c-.13,.05-.27-.03-.31-.16l-2.42-8.82c.79-.18,1.54-.47,2.23-.85l1.91,4.01c.06,.13,0,.28-.13,.32l-.71,.26,1.87,4.08c.06,.13,0,.27-.13,.32l-2.3,.84h0Z" fill="#191919" fillRule="evenodd"/>
            </svg>
            Okta
          </OAuthLoginButton>
        )}
      </div>
    </div>
  );
};
